/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect } from "react";
import { performanceConfig } from "@/config/performance";

type Rating = "good" | "needs-improvement" | "poor";

interface WebVitalMetric {
  name: string;
  value: number;
  rating: Rating;
}

interface PerformanceLayoutShift extends PerformanceEntry {
  value: number;
}

const METRICS_INTERVAL_MS = 1000;
const CLS_MULTIPLIER = 2.5;
const TIMING_MULTIPLIER = 1.75;

const getRating = (metricName: string, value: number): Rating => {
  const threshold =
    performanceConfig.thresholds[
      metricName as keyof typeof performanceConfig.thresholds
    ];

  if (!threshold) return "good";

  if (value <= threshold) return "good";

  const upperBound =
    metricName === "CLS"
      ? threshold * CLS_MULTIPLIER
      : threshold * TIMING_MULTIPLIER;

  return value <= upperBound ? "needs-improvement" : "poor";
};

const getRatingColor = (rating: Rating): string => {
  const colors: Record<Rating, string> = {
    good: "rgba(0, 128, 0, 0.1)",
    "needs-improvement": "rgba(255, 165, 0, 0.1)",
    poor: "rgba(255, 0, 0, 0.1)",
  };
  return colors[rating];
};

const collectMetrics = (): WebVitalMetric[] => {
  const metrics: WebVitalMetric[] = [];

  const fcpEntry = performance
    .getEntriesByType("paint")
    .find((e) => e.name === "first-contentful-paint");
  if (fcpEntry) {
    metrics.push({
      name: "FCP",
      value: fcpEntry.startTime,
      rating: getRating("FCP", fcpEntry.startTime),
    });
  }

  const lcpEntry = performance
    .getEntriesByType("largest-contentful-paint")
    .find((e) => e.entryType === "largest-contentful-paint");
  if (lcpEntry) {
    metrics.push({
      name: "LCP",
      value: lcpEntry.startTime,
      rating: getRating("LCP", lcpEntry.startTime),
    });
  }

  const clsEntries = performance.getEntriesByType(
    "layout-shift",
  ) as PerformanceLayoutShift[];
  const cls = clsEntries.reduce((acc, entry) => acc + entry.value, 0);
  metrics.push({ name: "CLS", value: cls, rating: getRating("CLS", cls) });

  // DD-06: INP replaces the deprecated FID metric (web-vitals v4).
  // Filter to entries with interactionId > 0 (user-initiated interactions only)
  // and use entry.duration which is the correct INP measurement (processingStart +
  // processingEnd - startTime was wrong — it double-counts processing time).
  const eventEntries = performance.getEntriesByType(
    "event",
  ) as PerformanceEventTiming[];
  const interactionEntries = eventEntries.filter((e) => e.interactionId > 0);
  if (interactionEntries.length > 0) {
    const inpValue = Math.max(...interactionEntries.map((e) => e.duration));
    if (Number.isFinite(inpValue) && inpValue >= 0) {
      metrics.push({
        name: "INP",
        value: inpValue,
        rating: getRating("INP", inpValue),
      });
    }
  }

  const navEntry = performance
    .getEntriesByType("navigation")
    .find((e) => e.entryType === "navigation") as
    | PerformanceNavigationTiming
    | undefined;
  if (navEntry?.responseStart !== undefined) {
    const ttfbValue = navEntry.responseStart - navEntry.requestStart;
    metrics.push({
      name: "TTFB",
      value: ttfbValue,
      rating: getRating("TTFB", ttfbValue),
    });
  }

  return metrics;
};

const styles = {
  container: {
    position: "fixed" as const,
    bottom: "20px",
    right: "20px",
    zIndex: 9999,
    backgroundColor: "white",
    border: "1px solid #ccc",
    borderRadius: "8px",
    padding: "12px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    maxWidth: "350px",
    fontSize: "14px",
  },
  heading: {
    margin: "0 0 8px 0",
    fontSize: "16px",
    fontWeight: "bold" as const,
  },
  instructions: { margin: "0 0 8px 0", fontSize: "12px", color: "#666" },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontSize: "13px",
  },
  th: {
    textAlign: "left" as const,
    borderBottom: "1px solid #eee",
    padding: "4px 8px",
  },
  tr: { borderBottom: "1px solid #eee" },
  td: { padding: "4px 8px" },
};

const PerformanceMonitor: React.FC = () => {
  const [metrics, setMetrics] = useState<WebVitalMetric[]>([]);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (import.meta.env.PROD) return;

    const intervalId = setInterval(() => {
      const collected = collectMetrics();
      if (collected.length > 0) setMetrics(collected);
    }, METRICS_INTERVAL_MS);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "P") {
        setIsVisible((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  if (import.meta.env.PROD || !isVisible || metrics.length === 0) {
    return null;
  }

  return (
    <div className="performance-monitor" style={styles.container}>
      <h3 style={styles.heading}>Performance Metrics</h3>
      <p style={styles.instructions}>Press Ctrl+Shift+P to toggle this panel</p>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Metric</th>
            <th style={styles.th}>Value</th>
            <th style={styles.th}>Rating</th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((metric) => (
            <tr
              key={metric.name}
              style={{
                ...styles.tr,
                backgroundColor: getRatingColor(metric.rating),
              }}
            >
              <td style={styles.td}>{metric.name}</td>
              <td style={styles.td}>
                {metric.name === "CLS"
                  ? metric.value.toFixed(3)
                  : `${Math.round(metric.value)}ms`}
              </td>
              <td style={styles.td}>{metric.rating}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PerformanceMonitor;
